import React, { useCallback, useEffect, useRef, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import ru from "date-fns/locale/ru";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBarcode,
  faCamera,
  faImage,
  faWindowClose,
} from "@fortawesome/free-solid-svg-icons";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  InputGroup,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";
import { Link, useHistory, useLocation } from "react-router-dom";
import Select from "react-select";
import moment from "moment";

import "react-datepicker/dist/react-datepicker.css";

import {
  getSupplies,
  getSupplyByNumber,
  getSupplyStatuses,
  getSupplyToExcel,
  setSupplyStatus,
} from "services/apiSupply";
import { MySpinner } from "components/MySpinner";
import { Pagination } from "components/Pagination";
import useQuery from "utils/useQuery";
import { SuppliesTable } from "features/Supply/SuppliesTable";
import { dateFormatU, dateTimeFormat } from "utils/format";
import { SelectProvider } from "components/SelectProvider";
import { priceFormat } from "utils/format";
import { Checkbox } from "components/Checkbox";
import { useUser } from "features/Users/UserContext";
import { useOrders } from "features/Orders/OrdersContext";
import { CloseButton } from "components/CloseButton";
import { GetDocFromDiadocModal } from "features/Supply/GetDocFromDiadocModal";
import { REGULATED } from "pages/orders";
import { toast } from "react-toastify";
import { scanBarcodeFromImageFile } from "utils/supplyListBarcodeScan";

import "./supplies.scss";

registerLocale("ru", ru);

export const SuppliesPage = () => {
  const history = useHistory();
  const { pathname } = useLocation();
  const query = useQuery(useLocation);
  const { admins } = useUser();
  const { setStatuses } = useOrders();
  const [supplies, setSupplies] = useState([]);
  const [suppliesCount, setSuppliesCount] = useState(0);
  const [suppliesSumm, setSuppliesSumm] = useState(0);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(
    query.get("page") ? Number(query.get("page")) : 1
  );
  const getPageSize = localStorage.getItem("pageSize") ? JSON.parse(localStorage.getItem("pageSize")) : {};
  const [pageSize, setPageSize] = useState(getPageSize[pathname] || 10);
  const [provider, setProvider] = useState(
    query.get("provider")
      ? { label: query.get("providerName"), value: query.get("provider") }
      : null
  );
  const [search, setSearch] = useState(query.get("search") || "");
  const [dateStart, setDateStart] = useState(
    query.get("dateStart") ? new Date(query.get("dateStart")) : moment().startOf('year').toDate()
  );
  const [dateEnd, setDateEnd] = useState(
    query.get("dateEnd") ? new Date(query.get("dateEnd")) : ""
  );
  const [carriedOut, setCarriedOut] = useState(
    query.get("carriedOut") || false
  );

  const [number, setNumber] = useState(query.get("number") || "");
  const [isShowReset, setIsShowReset] = useState(false);
  const [factNum, setFactNum] = useState(query.get("factNum") || "");

  const [admin, setAdmin] = useState(
    localStorage.getItem("filterAdmin")
      ? JSON.parse(localStorage.getItem("filterAdmin"))
      : null
  );

  const [regulated, setRegulated] = useState(
    query.get("regulated")
      ? { label: query.get("regulatedName"), value: query.get("regulated") }
      : null
  );

  const barcodeGalleryRef = useRef(null);
  const barcodeVideoRef = useRef(null);
  const barcodeStreamRef = useRef(null);
  const [barcodeModalOpen, setBarcodeModalOpen] = useState(false);
  const [barcodeCameraMode, setBarcodeCameraMode] = useState(false);

  const canUseBarcodeCamera =
    typeof navigator !== "undefined" &&
    navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === "function";

  const stopBarcodeCameraStream = useCallback(() => {
    const stream = barcodeStreamRef.current;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      barcodeStreamRef.current = null;
    }
    const el = barcodeVideoRef.current;
    if (el) {
      el.srcObject = null;
    }
  }, []);

  const closeBarcodeModal = useCallback(() => {
    stopBarcodeCameraStream();
    setBarcodeModalOpen(false);
    setBarcodeCameraMode(false);
  }, [stopBarcodeCameraStream]);

  useEffect(() => {
    if (!barcodeModalOpen || !barcodeCameraMode) {
      return;
    }
    let active = true;
    let stream = null;
    const videoEl = barcodeVideoRef.current;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });
        if (!active) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        barcodeStreamRef.current = stream;
        const el = barcodeVideoRef.current;
        if (el) {
          el.srcObject = stream;
          await el.play();
        }
      } catch (e) {
        console.warn(e);
        if (active) {
          toast.error(
            "Камера недоступна. Разрешите доступ в настройках браузера или выберите «Из галереи»."
          );
          setBarcodeCameraMode(false);
        }
      }
    })();
    return () => {
      active = false;
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
      }
      barcodeStreamRef.current = null;
      if (videoEl) {
        videoEl.srcObject = null;
      }
    };
  }, [barcodeModalOpen, barcodeCameraMode]);

  useEffect(
    () => () => {
      stopBarcodeCameraStream();
    },
    [stopBarcodeCameraStream]
  );

  useEffect(() => {
    setLoading(true);
    getAndSetStatuses();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAndSetSupplies = () => {
    if (loading) return;
    getSupplies({
      paginationCount: pageSize,
      paginationOffset: (currentPage - 1) * pageSize,
      search,
      dateStart: dateFormatU(dateStart),
      dateEnd: dateFormatU(dateEnd),
      number,
      provider: provider?.value,
      admin: admin?.value,
      carriedOut: carriedOut ? 1 : 0,
      factNum,
      regulated: regulated?.value
    })
      .then((res) => {
        setSupplies(res.items);
        setSuppliesCount(res.totalItems);
        setSuppliesSumm(res.totalSumm);
      })
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setLoading(true);
    getAndSetSupplies();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize]);

  const getAndSetStatuses = () => {
    getSupplyStatuses()
      .then((res) => {
        setStatuses(res);
      })
      .catch((e) => console.error(e));
  };

  useEffect(() => {
    const url = new URLSearchParams();
    if (currentPage !== 1) {
      url.append("page", currentPage);
    }

    if (search) {
      url.append("search", search);
    }

    if (provider?.value) {
      url.append("provider", provider.value);
      url.append("providerName", provider.label);
    }

    if (dateStart) {
      url.append("dateStart", dateFormatU(dateStart));
    }

    if (dateEnd) {
      url.append("dateEnd", dateFormatU(dateEnd));
    }

    if (number) {
      url.append("number", number);
    }

    if (carriedOut) {
      url.append("carriedOut", carriedOut);
    }

    if (factNum) {
      url.append("factNum", factNum);
    }

    if (regulated !== null) {
      url.append("regulated", regulated?.value);
      url.append("regulatedName", regulated?.label);
    }

    if (url) {
      history.push(`${pathname}?${url}`);
    } else {
      history.push(pathname);
    }

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    pageSize,
    search,
    provider,
    dateStart,
    dateEnd,
    number,
    carriedOut,
    factNum,
    regulated
  ]);

  useEffect(() => {
    setLoading(true);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectHandler = (val, res) => {
    switch (res.name) {
      case "admin":
        localStorage.setItem("filterAdmin", JSON.stringify(val));
        setAdmin(val);
        break;
      case "regulated":
        setRegulated(val);
        break;
      default:
        break;
    }
  };

  const statusHandler = (status, supply) => {
    setLoading(true);
    setSupplyStatus({
      id: supply?.id,
      statusId: status?.id,
    })
      .then(() => {
        getAndSetSupplies();
      })
      .catch((e) => {
        console.error(e);
      });
  };

  const isSearch = () => {
    if (
      search ||
      dateStart ||
      dateEnd ||
      number ||
      provider?.value ||
      admin?.value ||
      carriedOut ||
      factNum ||
      regulated
    ) {
      return true;
    }
    return false;
  };

  const searchHandler = () => {
    if (!isSearch()) return;
    if (currentPage !== 1) {
      setCurrentPage(1);
      return;
    }

    setLoading(true);
    getAndSetSupplies();
  };

  const handleKeypress = (e) => {
    if (e.keyCode === 13) {
      e.preventDefault();
      searchHandler();
    }
  };

  useEffect(() => {
    setIsShowReset(isSearch());
    if (!isSearch()) {
      setLoading(true);
      getAndSetSupplies();
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    search,
    dateStart,
    dateEnd,
    number,
    provider,
    admin,
    carriedOut,
    factNum,
    regulated
  ]);

  const resetFilter = () => {
    setProvider(null);
    setSearch("");
    setDateStart("");
    setDateEnd("");
    setNumber("");
    setAdmin(null);
    setCarriedOut(null);
    setFactNum("");
    setRegulated(null);
    localStorage.removeItem("filterAdmin");
  };

  const numberHandler = (e) => {
    setNumber(e.target.value.replace(/[^+\d]/g, ""));
  };

  const processScannedBarcodeFromFile = async (file) => {
    setLoading(true);
    try {
      let raw;
      try {
        raw = await scanBarcodeFromImageFile(file);
      } catch (err) {
        console.warn(err);
        raw = null;
      }
      if (!raw || !String(raw).trim()) {
        toast.error(
          "Штрихкод не распознан. Крупнее кадр, ровный свет. Логи: localStorage.setItem(\"DEBUG_SUPPLY_BARCODE\",\"1\")"
        );
        return;
      }
      const trimmed = String(raw).trim();
      if (trimmed.length < 2) {
        toast.error("Код слишком короткий после снятия префикса.");
        return;
      }
      const numberForApi = trimmed.slice(1).replace(/[^+\d]/g, "");
      if (!numberForApi) {
        toast.error("Нет цифр для номера поступления (первый символ отброшен).");
        return;
      }
      const supply = await getSupplyByNumber(numberForApi);
      if (supply == null || supply.id == null) {
        toast.error("Поступление не найдено по номеру из штрихкода.");
        return;
      }
      history.push(`/receipt/supply/${supply.id}`);
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message;
      toast.error(msg || "Ошибка при распознавании или загрузке поступления");
    } finally {
      setLoading(false);
    }
  };

  const onBarcodeGalleryChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) {
      return;
    }
    await processScannedBarcodeFromFile(file);
  };

  const openBarcodeGalleryPicker = () => {
    closeBarcodeModal();
    window.setTimeout(() => barcodeGalleryRef.current?.click(), 150);
  };

  const openBarcodeCameraInModal = () => {
    if (!canUseBarcodeCamera) {
      toast.error("Камера в этом браузере недоступна. Выберите «Из галереи».");
      return;
    }
    setBarcodeCameraMode(true);
  };

  const captureBarcodePhoto = () => {
    const video = barcodeVideoRef.current;
    if (!video || video.readyState < 2 || !video.videoWidth) {
      toast.error("Камера ещё не готова, подождите секунду.");
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob(
      async (blob) => {
        closeBarcodeModal();
        if (!blob) {
          toast.error("Не удалось снять кадр.");
          return;
        }
        const file = new File([blob], "barcode-camera.jpg", {
          type: "image/jpeg",
        });
        await processScannedBarcodeFromFile(file);
      },
      "image/jpeg",
      0.92
    );
  };

  const backFromBarcodeCamera = () => {
    stopBarcodeCameraStream();
    setBarcodeCameraMode(false);
  };

  const downloadSupplyToExcel = () => {
    setLoading(true);
    getSupplyToExcel({
      search,
      dateStart: dateFormatU(dateStart),
      dateEnd: dateFormatU(dateEnd),
      number,
      provider: provider?.value,
      admin: admin?.value,
      carriedOut: carriedOut ? 1 : 0,
      factNum,
      regulated: regulated?.value
    })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `Поступления_${dateTimeFormat(new Date())}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Container fluid className="p-0 supplies">
      {loading && <MySpinner overlay />}
      <Card className="mb-0 filter">
        <CardBody>
          <h1 className="h3 mb-3">Поступления</h1>

          <Form className="mb-3">
            <Row>
              <Col>
                <Label>Дата</Label>
                <InputGroup className="daterange">
                  <DatePicker
                    placeholderText="от"
                    selected={dateStart}
                    onChange={setDateStart}
                    selectsStart
                    startDate={dateStart}
                    endDate={dateEnd}
                    dateFormat="dd.MM.yyyy"
                    locale="ru"
                    className="form-control date-start"
                  />
                  <DatePicker
                    placeholderText="до"
                    selected={dateEnd}
                    onChange={setDateEnd}
                    selectsEnd
                    startDate={dateStart}
                    endDate={dateEnd}
                    minDate={dateStart}
                    dateFormat="dd.MM.yyyy"
                    locale="ru"
                    className="form-control date-end"
                  />
                </InputGroup>
              </Col>
              <Col>
                <Label>Поступление №</Label>
                <Input
                  name="number"
                  placeholder="По номеру"
                  value={number}
                  onChange={numberHandler}
                  onKeyUp={handleKeypress}
                />
              </Col>
              <Col md="auto" className="supplies__barcode-open-col">
                <Label className="text-nowrap">Открыть по штрихкоду</Label>
                <Button
                  type="button"
                  color="secondary"
                  className="supplies__open-by-barcode d-flex align-items-center"
                  onClick={() => setBarcodeModalOpen(true)}
                  title="Сканировать штрихкод: камера или галерея"
                  aria-label="Открыть поступление по штрихкоду"
                >
                  <FontAwesomeIcon icon={faBarcode} className="mr-50" size="lg" />
                  Штрихкод
                </Button>
                <input
                  ref={barcodeGalleryRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/*"
                  className="d-none"
                  onChange={onBarcodeGalleryChange}
                />
              </Col>
              <Col>
                <Label>Артикул</Label>
                <Input
                  name="search"
                  placeholder="По артикулу"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyUp={handleKeypress}
                />
              </Col>
              <Col>
                <Label>Поставщик</Label>
                <SelectProvider provider={provider} setProvider={setProvider} />
              </Col>
            </Row>
            <Row>
              <Col md={3}>
                <Label>Создал</Label>
                <Select
                  name="admin"
                  placeholder="Создал поступление"
                  className="react-select-container"
                  classNamePrefix="react-select"
                  options={admins}
                  value={admin}
                  onChange={selectHandler}
                  isClearable
                />
              </Col>
              <Col md={3}>
                <Label>По номеру сч. фактуры</Label>
                <Input
                  name="factNum"
                  placeholder="По номеру сч. фактуры"
                  value={factNum}
                  onChange={(e) => setFactNum(e.target.value)}
                  onKeyUp={handleKeypress}
                />
              </Col>
              <Col md={1}>
                <Label>Проведено</Label>
                <Checkbox
                  onChange={(e) => setCarriedOut(e.target.checked)}
                  checked={carriedOut}
                >
                  Проведено
                </Checkbox>
              </Col>
              <Col md={1}>
                <Label>Регл.</Label>
                <Select
                  name="regulated"
                  placeholder="Регл."
                  className="react-select-container"
                  classNamePrefix="react-select"
                  options={REGULATED}
                  value={regulated}
                  onChange={selectHandler}
                  isClearable
                />
              </Col>
              <Col>
                <InputGroup className="with-label">
                  {isShowReset && (
                    <Button color="danger" onClick={() => resetFilter()}>
                      <FontAwesomeIcon icon={faWindowClose} />
                    </Button>
                  )}
                  <Button
                    color="primary"
                    onClick={searchHandler}
                    className="button-wide"
                  >
                    Поиск
                  </Button>
                </InputGroup>
              </Col>
            </Row>
            <Row>
              <Col md={2}>
                Поступлений: {suppliesCount} на сумму{" "}
                {priceFormat(parseFloat(suppliesSumm))}
              </Col>
              <Col md={2}>
                <Button onClick={downloadSupplyToExcel}>Скачать Excel</Button>
              </Col>
              <Col md={2}>
                <GetDocFromDiadocModal />
              </Col>
            </Row>
          </Form>

          <Row>
            <Col md="2">
              <Link
                to={`/receipt/supply/new`}
                color="primary"
                className="button-wide btn btn-primary"
              >
                Добавить поступление
              </Link>
            </Col>
          </Row>
        </CardBody>
      </Card>

      <div>
        <SuppliesTable supplies={supplies} statusHandler={statusHandler} />

        <Card>
          <CardBody>
            <Pagination
              totalCount={suppliesCount}
              currentPage={currentPage}
              pageSize={pageSize}
              setCurrentPage={setCurrentPage}
              onChangePageSize={setPageSize}
            />
          </CardBody>
        </Card>
      </div>

      <Modal
        isOpen={barcodeModalOpen}
        toggle={closeBarcodeModal}
        centered
        className="supplies__barcode-source-modal"
      >
        <ModalHeader className="supplies__barcode-source-modal__header">
          <span className="supplies__barcode-source-modal__title">
            {barcodeCameraMode
              ? "Снимите штрихкод"
              : "Камера или галерея"}
          </span>
          <CloseButton onClick={closeBarcodeModal} />
        </ModalHeader>
        <ModalBody>
          {barcodeCameraMode ? (
            <>
              <video
                ref={barcodeVideoRef}
                className="supplies__barcode-video"
                playsInline
                muted
                autoPlay
              />
              <div className="supplies__barcode-camera-actions">
                <Button color="primary" onClick={captureBarcodePhoto}>
                  <FontAwesomeIcon icon={faCamera} className="mr-50" />
                  Снять фото
                </Button>
                <Button color="secondary" outline onClick={backFromBarcodeCamera}>
                  Назад
                </Button>
              </div>
            </>
          ) : (
            <>
              {canUseBarcodeCamera ? (
                <Button
                  color="primary"
                  block
                  className="mb-2"
                  onClick={openBarcodeCameraInModal}
                >
                  <FontAwesomeIcon icon={faCamera} className="mr-50" />
                  Снять камерой
                </Button>
              ) : null}
              <Button
                color="secondary"
                outline
                block
                onClick={openBarcodeGalleryPicker}
              >
                <FontAwesomeIcon icon={faImage} className="mr-50" />
                Из галереи
              </Button>
              {!canUseBarcodeCamera ? (
                <p className="text-muted small mb-0 mt-2">
                  В этом браузере нет доступа к камере через сайт — используйте
                  «Из галереи» или другой браузер (Chrome на Android обычно
                  поддерживает камеру по HTTPS).
                </p>
              ) : null}
            </>
          )}
        </ModalBody>
      </Modal>
    </Container>
  );
};
